import { inject } from '@angular/core';
import { CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';

import { map } from 'rxjs/operators';

import { APP_PATHS_SLASH } from '@app/core/enums/paths/app.paths';
import { AuthStore } from '@store/auth.store';

/**
 * Lets a route through when the access token is valid, refreshes it once (through the store's
 * shared request, so a guard and a burst of 401s rotate the refresh token only once) when it is
 * not, and otherwise sends the visitor to the login page remembering the URL they asked for. A
 * stale refresh (the session changed while it ran) defers to the session now in place.
 */
export const authGuard: CanActivateFn = (_route, state: RouterStateSnapshot) => {
  const authStore = inject(AuthStore);
  const router = inject(Router);

  const redirectToLogin = (): false => {
    authStore.rememberRedirect(state.url);
    void router.navigate([APP_PATHS_SLASH.SIGN_IN]);
    return false;
  };
  const hasValidSession = (): boolean =>
    authStore.isAuthenticated() && !authStore.isAccessTokenExpired();

  if (hasValidSession()) {
    return true;
  }

  if (authStore.hasRefreshToken()) {
    return authStore
      .refresh()
      .pipe(
        map(
          outcome =>
            outcome === 'refreshed' ||
            (outcome === 'stale' && hasValidSession()) ||
            redirectToLogin()
        )
      );
  }

  return redirectToLogin();
};
